import { useEffect, useRef, useState } from 'react';

import type {
  DrilldownParams,
  DrilldownSectionData,
  ExecutionsHistoryQuery,
  ExecutionsHistorySectionResult,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ExecutionsHistoryDashboardService from '@src/services/ExecutionsHistoryDashboardService';

export interface HistoryDrilldownState {
  loading: boolean;
  result?: ExecutionsHistorySectionResult<DrilldownSectionData>;
}

/**
 * Loads one page of the `drilldown` section while `params` is set. Responses for older
 * params (another target, page or search) are discarded.
 */
export const useHistoryDrilldown = (query: ExecutionsHistoryQuery, params: DrilldownParams | null) => {
  const serviceRef = useRef<ExecutionsHistoryDashboardService | null>(null);
  if (!serviceRef.current) serviceRef.current = new ExecutionsHistoryDashboardService();
  const generationRef = useRef(0);
  const [retryCount, setRetryCount] = useState(0);
  const [state, setState] = useState<HistoryDrilldownState>({ loading: false });

  const requestKey = params ? JSON.stringify({ query, params, retryCount }) : null;

  useEffect(() => {
    generationRef.current += 1;
    if (!params) {
      setState({ loading: false });
      return;
    }
    const generation = generationRef.current;
    setState({ loading: true });
    (serviceRef.current as ExecutionsHistoryDashboardService)
      .getSection<DrilldownSectionData>('drilldown', query, params)
      .then(result => {
        if (generation === generationRef.current) setState({ loading: false, result });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestKey captures query and params content
  }, [requestKey]);

  return { ...state, dataResetKey: requestKey, retry: () => setRetryCount(count => count + 1) };
};
